/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import { t } from "i18n";
import FieldLabel from "components/field-label/field-label";
import Facebook from "components/icons/facebook";
import Google from "components/icons/google";
import Linkedin from "components/icons/linkedin";
import TwitterSquare from "components/icons/twitter-square";
import Button from "components/button/button";
import styles from "./login-social-and-signin-actions.module.css";
import WithGAEvent from "helpers/ga-analytics/with-ga-event";
import { getSessionRedirectUri } from "./utils";

interface Props {
  formId?: string;
  submitData: () => void;
  cancelFormSignIn: () => void;
  isSignInDisabled: boolean;
  isSocialLoginDisabled: boolean;
  isBridgekeeperIntegrationEnabled: boolean;
}

const socialLogins = {
  facebook: Facebook,
  google: Google,
  twitter: TwitterSquare,
  linkedin: Linkedin
};

const socialLoginButton = (isBridgekeeperIntegrationEnabled: boolean, provider: string) => {
  const SocialLogin = socialLogins[provider];
  const redirectUri = getSessionRedirectUri();
  const postLoginRedirectUri = `${window.location.origin}${window.location.pathname}?redirect_uri=${redirectUri}`;
  const redirectParam = redirectUri ? `&redirect-url=${postLoginRedirectUri}` : "";
  return (
    <WithGAEvent label={`${provider}-login`}>
      <a
        href={
          isBridgekeeperIntegrationEnabled
            ? `/api/auth/v1/login?auth-provider=${provider}${redirectParam}`
            : `/login?auth-provider=${provider}`
        }
        className={styles["social-icon"]}>
        <SocialLogin width={32} height={32} />
      </a>
    </WithGAEvent>
  );
};

const LoginSocialAndSignInActions: React.SFC<Props> = ({
  formId,
  submitData,
  cancelFormSignIn,
  isSignInDisabled,
  isSocialLoginDisabled,
  isBridgekeeperIntegrationEnabled
}) => {
  return (
    <div className={styles["social-and-signin-container"]}>
      <div className={styles["signin-container"]}>
        <WithGAEvent label="form-signin">
          <Button
            form={formId}
            testId="signin-button"
            type="primary"
            onClick={() => submitData()}
            disabled={isSignInDisabled}
            classname={"social-and-signin-container-button"}>
            {t("loginPage.sign_in")}
          </Button>
        </WithGAEvent>
        <WithGAEvent label="form-cancel-signin">
          <Button type="secondary" onClick={() => cancelFormSignIn()} classname={"social-and-signin-container-button"}>
            {t("loginPage.cancel")}
          </Button>
        </WithGAEvent>
      </div>
      {!isSocialLoginDisabled && (
        <React.Fragment>
          <FieldLabel label={t("loginPage.or_sign_in_with")} />
          <div className={styles["social-container"]}>
            {socialLoginButton(isBridgekeeperIntegrationEnabled, "facebook")}
            {socialLoginButton(isBridgekeeperIntegrationEnabled, "google")}
            {socialLoginButton(isBridgekeeperIntegrationEnabled, "twitter")}
            {socialLoginButton(isBridgekeeperIntegrationEnabled, "linkedin")}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default LoginSocialAndSignInActions;
